import type { Location, LocationUpdate, NewLocation } from "@hobbyshopify/backend-client";
import type { GeoRegionList } from "../repositories/countries-repository";

/** The branch form: every column a string, so an input can bind to it. */
export interface LocationDraft {
  readonly name: string;
  readonly phone: string;
  readonly openingHours: string;
  readonly address1: string;
  readonly address2: string;
  readonly city: string;
  readonly province: string;
  readonly zip: string;
  readonly countryCode: string;
}

export type LocationField = keyof LocationDraft;
export type LocationErrors = Partial<Record<LocationField, string>>;

export const EMPTY_LOCATION: LocationDraft = {
  name: "",
  phone: "",
  openingHours: "",
  address1: "",
  address2: "",
  city: "",
  province: "",
  zip: "",
  countryCode: "",
};

/** Prefills the form from a saved location; the empty form without one. */
export function locationDraft(location?: Location | null): LocationDraft {
  if (!location) return EMPTY_LOCATION;
  return {
    name: location.name,
    phone: location.phone ?? "",
    openingHours: location.openingHours ?? "",
    address1: location.address1 ?? "",
    address2: location.address2 ?? "",
    city: location.city ?? "",
    province: location.province ?? "",
    zip: location.zip ?? "",
    // Blank for every location created before the country became required: the
    // merchant must pick one to save, which is the migration path for those rows.
    countryCode: location.countryCode ?? "",
  };
}

function trimAll(draft: LocationDraft): LocationDraft {
  return Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v.trim()])) as unknown as LocationDraft;
}

/**
 * The address rules, mirroring `LocationValidation.resolveAddress` on the server.
 * The server is the authority; this exists so the merchant is told which field is
 * wrong while still looking at it. `regions` is the draft's country's list: a
 * state is required where the country has one and meaningless where it does not.
 */
export function validateLocationDraft(draft: LocationDraft, regions: GeoRegionList): LocationErrors {
  const d = trimAll(draft);
  const errors: LocationErrors = {};
  if (!d.name) errors.name = "Give this location a name.";
  if (!d.address1) errors.address1 = "Enter the street address.";
  if (!d.city) errors.city = "Enter the town or city.";
  if (!d.countryCode) errors.countryCode = "Choose the country this shop is in.";
  if (regions.length > 0) {
    if (!d.province) errors.province = "Choose the state or province.";
    // The select only offers valid codes, so this catches a stale value left
    // behind by switching country after picking a state.
    else if (!regions.some((region) => region.code === d.province)) {
      errors.province = "Choose a state or province from the list.";
    }
  }
  return errors;
}

const OPTIONAL_FIELDS = ["phone", "openingHours", "address2", "province", "zip"] as const;

/** Create bodies omit a blank optional field rather than send "": the backend's optional check only skips absent values. */
export function draftToNewLocation(draft: LocationDraft): NewLocation {
  const d = trimAll(draft);
  const request: NewLocation = { name: d.name, address1: d.address1, city: d.city, countryCode: d.countryCode };
  for (const field of OPTIONAL_FIELDS) {
    if (d[field]) request[field] = d[field];
  }
  return request;
}

/** Edits send every field, blanks included: omitting one on a PATCH means "leave it alone", which would make clearing impossible. */
export function draftToLocationUpdate(draft: LocationDraft): LocationUpdate {
  return { ...trimAll(draft) };
}

export function formatLocationAddress(location: Location): string {
  return [location.address1, location.address2, location.city, location.province, location.zip, location.country]
    .filter(Boolean)
    .join(", ");
}
